import React from 'react';
import { CardContent } from '@/components/ui/card';
import { TableColumn } from '../../agent-performance/tableConfig';
import { AgentMetricsTableHeaderRow } from './AgentMetricsTableHeaderRow';
import { AgentMetricsTableRow } from './AgentMetricsTableRow';
import { AgentMetricsData } from '../../agent-performance/agentTableTypes';
import { useStickyRow } from '../hooks';
import { TableGridRow, TableGridCell } from '@/components/ui/table';

interface AgentMetricsTableContentProps {
  data: AgentMetricsData[];
  columns: TableColumn[];
  isLoading?: boolean;
  onToggleExpansion?: (agentId: number) => void;
  onCellClick?: (
    columnKey: string,
    handledCallIds: string[],
    transferredCallIds: string[],
    callIdsSLA?: string[],
    totalSLACalls?: number,
    agentName?: string,
    queueName?: string
  ) => void;
}

export const AgentMetricsTableContent: React.FC<AgentMetricsTableContentProps> = ({
  data,
  columns,
  isLoading = false,
  onToggleExpansion,
  onCellClick,
}) => {
  const { stickyRowIndex, scrollContainerRef } = useStickyRow({ data });
  const skeletonRowIds = React.useMemo(() => Array.from({ length: 9 }, () => crypto.randomUUID()), []);

  return (
    <CardContent className="p-0 h-full min-h-0">
      <div className="relative overflow-hidden h-full min-h-0">
        <div
          ref={scrollContainerRef}
          className="w-full h-full min-h-0 overflow-x-auto overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 [&::-webkit-scrollbar-corner]:bg-transparent"
        >
          <div className="table w-full">
            <AgentMetricsTableHeaderRow columns={columns} />
            <div className="table-row-group">
              {isLoading ? (
                skeletonRowIds.map((rowId, idx) => (
                  <TableGridRow key={rowId} index={idx} className="border-b-0">
                    {columns.map((col, colIndex) => (
                      <TableGridCell
                        key={col.key}
                        className="h-[45px] px-2 py-3 border-gray-300 dark:border-0 align-middle"
                        style={{
                          minWidth: colIndex === 0 ? '200px' : col.minWidth,
                          maxWidth: colIndex === 0 ? '265px' : col.maxWidth,
                          width: colIndex === 0 ? '240px' : `calc((100% - 240px) / ${columns.length - 1})`
                        }}
                      >
                        <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
                      </TableGridCell>
                    ))}
                  </TableGridRow>
                ))
              ) : data.length === 0 ? (
                <div className="table-row mt-2">
                  {columns.map((col, index) => (
                    <div key={col.key} className={`table-cell py-3 px-2 text-center`}>
                      {index === Math.floor(columns.length / 2) && (
                        <p className="text-sm dark:text-white text-muted-foreground mt-6">No call records were found</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                data.map((row, idx) => (
                  <AgentMetricsTableRow
                    key={row.id || idx}
                    data={row}
                    columns={columns}
                    index={idx}
                    onToggleExpansion={onToggleExpansion}
                    isSticky={stickyRowIndex === idx}
                    onCellClick={onCellClick}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </CardContent>
  );
};
